import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { getTabList } from '../helpers/tableHelpers';
import { getTabsForSource } from '../helpers/sourceListHelpers';
import { getSourceDisplayName, getTabDisplayInfo } from '../helpers/sourceTabMetadata';
import { formatRecordCount, useSourceTabStats } from '../helpers/sourceTabStats';
import SharedBadge from '@/global/Badge/Badge';

type SourceNotInTabViewProps = {
  source: string;
  tab: string;
  view: string;
};

export function SourceNotInTabView({ source, tab, view }: SourceNotInTabViewProps) {
  const navigate = useNavigate();

  const sourceLabel = getSourceDisplayName(source);
  const currentTabInfo = getTabDisplayInfo(tab);

  const availableTabs = useMemo(() => {
    const sourceTabs = getTabsForSource(source);
    const viewTabKeys = getTabList(view, tab).map(t => t.key);
    return sourceTabs.filter(t => t !== tab && viewTabKeys.includes(t));
  }, [source, tab, view]);

  const recordCounts = useSourceTabStats(source, availableTabs, view, tab);

  const navigateToTab = (targetTab: string) => {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', targetTab);
    navigate({ pathname: window.location.pathname, search: `?${params.toString()}` });
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6 md:p-8 my-8 mr-4">
      <div className="flex flex-col gap-8 lg:flex-row lg:gap-0">
        <div className="min-w-0 flex-1 lg:pr-8">
          <SharedBadge
            text="No data in this tab"
            variant="secondary"
            size="sm"
            icon={<InformationCircleIcon className="h-3.5 w-3.5" aria-hidden />}
          />

          <h2 className="mt-4 text-xl font-bold leading-snug text-slate-900 sm:text-2xl">
            {sourceLabel} doesn&apos;t contribute to {currentTabInfo.label}
          </h2>

          <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">
            {sourceLabel} is not a source for <strong>{currentTabInfo.associationType}</strong> in DISGENET.
            This isn&apos;t an error — this source simply doesn&apos;t contain that type of evidence.
          </p>
        </div>

        {availableTabs.length > 0 && (
          <>
            <div className="hidden w-px shrink-0 bg-slate-200 lg:block" aria-hidden />

            <div className="min-w-0 flex-1 lg:pl-8">
              <p className="text-xs font-semibold uppercase tracking-wider text-pink">
                Available evidence from {sourceLabel}
              </p>

              <ul className="mt-3 space-y-2">
                {availableTabs.map(targetTab => {
                  const tabInfo = getTabDisplayInfo(targetTab);
                  const Icon = tabInfo.Icon;
                  const count = recordCounts[targetTab];

                  return (
                    <li key={targetTab}>
                      <button
                        type="button"
                        onClick={() => navigateToTab(targetTab)}
                        className="group flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-3 text-left transition-colors hover:border-slate-300 hover:bg-slate-50"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500">
                          <Icon className="h-4 w-4" aria-hidden />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="font-semibold text-slate-900">{tabInfo.label}</span>
                            {typeof count === 'number' && (
                              <span className="text-xs text-slate-400">
                                {formatRecordCount(count)} records
                              </span>
                            )}
                          </span>
                          <span className="mt-0.5 block text-xs text-slate-500">{tabInfo.description}</span>
                        </span>
                        <ChevronRightIcon
                          className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-slate-500"
                          aria-hidden
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
